import { TextField, TextFieldProps } from '@mui/material'
import * as React from 'react'
import { Controller, ControllerProps, FieldValues } from 'react-hook-form'
import { NumericFormat, NumericFormatProps } from 'react-number-format'

export type CurrencyInputProps = Omit<NumericFormatProps, 'type' | 'size' | 'format' | 'customInput'> &
  Omit<TextFieldProps, 'onChange' | 'value' | 'defaultValue' | 'size'>

/**
 * Currency input component for form fields using react-number-format.
 * Different from InlineCurrencyInput - this is designed for standard form fields.
 * Handles currency formatting with dollar prefix and returns numeric values in cents.
 */
const _CurrencyInput = (
  { onChange, name, ...props }: CurrencyInputProps,
  ref: React.Ref<HTMLInputElement>
) => (
  <NumericFormat
    getInputRef={ref}
    prefix="$"
    valueIsNumericString
    allowNegative={false}
    decimalScale={2}
    thousandSeparator
    customInput={TextField}
    onValueChange={({ value: v }, { event }) => {
      // Convert dollars to cents for storage
      const centsValue = v ? Math.round(parseFloat(v) * 100) : 0
      if (onChange) {
        onChange({
          ...event,
          target: { name, value: centsValue.toString() } as unknown as HTMLInputElement,
        } as React.ChangeEvent<HTMLInputElement>)
      }
    }}
    {...props}
    type="text"
  />
)

export const CurrencyInput = React.forwardRef(_CurrencyInput)

type CurrencyInputControllerProps<T extends FieldValues> = CurrencyInputProps &
  Pick<ControllerProps<T>, 'name' | 'control' | 'rules'>

export const CurrencyInputController = <T extends FieldValues>({
  name,
  control,
  rules,
  value,
  ...props
}: CurrencyInputControllerProps<T>) => (
  <Controller
    render={({ field, formState: { submitCount }, fieldState: { error, isTouched } }) => {
      const isError = Boolean((isTouched || submitCount > 0) && error?.message)
      // Convert cents to dollars for display
      const displayValue = field.value ? (parseFloat(field.value) / 100).toString() : ''
      return (
        <CurrencyInput
          {...props}
          {...field}
          value={displayValue}
          error={isError}
          helperText={isError ? error?.message : props.helperText}
        />
      )
    }}
    name={name}
    control={control}
    rules={rules}
  />
)
